import { useRef, useState } from "react";
import { Upload, Copy, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAssets, uploadAsset, assetUrl } from "./AssetPicker";

export const AssetsTab = () => {
  const { assets, reload } = useAssets();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadAsset(file);
      toast.success("Image uploaded");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const copy = (a) => {
    navigator.clipboard.writeText(assetUrl(a));
    toast.success("Image URL copied");
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.original_filename}?`)) return;
    await api.delete(`/admin/assets/${a.id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="admin-tab-assets">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">Asset library</h2>
          <p className="mt-1 text-sm text-faint">Upload images once, then pick them in podcasts, reels, hero & about photos.</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-outfit text-xs font-semibold text-sand hover:bg-terra">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload image
          <input ref={inputRef} data-testid="admin-assets-upload" type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {assets.map((a) => (
          <div key={a.id} data-testid={`admin-asset-${a.id}`} className="overflow-hidden rounded-2xl border border-ink/10 bg-white">
            <img src={assetUrl(a)} alt={a.original_filename} className="aspect-square w-full object-cover" loading="lazy" />
            <div className="p-3">
              <p className="truncate text-xs font-medium text-ink">{a.original_filename}</p>
              <p className="text-[10px] text-faint">{Math.round(a.size / 1024)} KB</p>
              <div className="mt-2 flex gap-2">
                <button data-testid={`admin-asset-copy-${a.id}`} onClick={() => copy(a)} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-ink/15 px-2 py-1.5 text-[11px] font-medium text-ink hover:border-terra hover:text-terra">
                  <Copy className="h-3 w-3" /> Copy URL
                </button>
                <button data-testid={`admin-asset-delete-${a.id}`} onClick={() => remove(a)} aria-label="Delete" className="rounded-lg border border-ink/15 px-2 py-1.5 text-faint hover:border-red-300 hover:text-red-500">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {assets.length === 0 && <p className="col-span-full py-10 text-center text-sm text-faint">No assets yet — upload the first image.</p>}
      </div>
    </div>
  );
};
